<template>
  <div>
    <div v-if="!hasEnoughData" class="text-caption text-disabled">
      Not enough data yet (need ≥ 20).
    </div>

    <div v-else class="normogram">
      <div class="normogram__chart">
        <div
          v-if="passMark != null"
          class="normogram__marker normogram__marker--pass"
          :style="{ left: `${passMarkX}%` }"
          title="Pass mark"
        />
        <div
          v-if="userScore != null"
          class="normogram__marker normogram__marker--user"
          :style="{ left: `${userX}%` }"
          title="You"
        />

        <div class="normogram__bars">
          <v-tooltip
            v-for="(bar, idx) in bars"
            :key="idx"
            :text="bar.tooltip"
            location="top"
            open-on-hover
            open-on-click
          >
            <template #activator="{ props: tooltipProps }">
              <div
                v-bind="tooltipProps"
                class="normogram__bar"
                :title="bar.tooltip"
              >
                <div
                  class="normogram__barFill"
                  :style="{ height: `${bar.heightPct}%` }"
                />
              </div>
            </template>
          </v-tooltip>
        </div>

        <div class="normogram__axis">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>

        <div class="text-caption text-disabled mt-2">
          Hover/tap a bar to see what percentage of users scored in that 5% band.
        </div>
      </div>

      <div class="normogram__meta text-caption">
        <div class="normogram__stats">
          <div v-if="mean != null" class="normogram__statRow">
            <div class="normogram__statLabel">Average score</div>
            <div class="normogram__statValue">{{ mean }}%</div>
          </div>

          <div v-if="p50 != null" class="normogram__statRow">
            <div class="normogram__statLabel">Typical score (median)</div>
            <div class="normogram__statValue">{{ p50 }}%</div>
          </div>

          <div v-if="p25 != null && p75 != null" class="normogram__statRow">
            <div class="normogram__statLabel">Most users (middle 50%)</div>
            <div class="normogram__statValue">{{ p25 }}%–{{ p75 }}%</div>
          </div>

          <div v-if="p10 != null && p90 != null" class="normogram__statRow">
            <div class="normogram__statLabel">Wider range (10%–90%)</div>
            <div class="normogram__statValue">{{ p10 }}%–{{ p90 }}%</div>
          </div>

          <div v-if="passMark != null" class="normogram__statRow">
            <div class="d-flex align-center ga-2">
              <span class="normogram__dot normogram__dot--pass" />
              <span>{{ passLabel || `Pass ${passMark}%` }}</span>
            </div>
          </div>

          <div v-if="userScore != null" class="normogram__statRow">
            <div class="d-flex align-center ga-2">
              <span class="normogram__dot normogram__dot--user" />
              <span>You: {{ userScoreRounded }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  bins: { type: Array, required: true },
  sampleSize: { type: Number, required: true },
  mean: { type: Number, default: null },
  p10: { type: Number, default: null },
  p25: { type: Number, default: null },
  p50: { type: Number, default: null },
  p75: { type: Number, default: null },
  p90: { type: Number, default: null },
  userScore: { type: Number, default: null },
  passMark: { type: Number, default: null },
  passLabel: { type: String, default: "" },
});

const hasEnoughData = computed(() => (props.sampleSize || 0) >= 20);

const safeBins = computed(() => {
  const raw = Array.isArray(props.bins) ? props.bins : [];
  const next = Array.from({ length: 21 }, (_, i) => {
    const v = Number(raw[i] ?? 0);
    return Number.isFinite(v) && v > 0 ? Math.round(v) : 0;
  });
  return next;
});

const maxCount = computed(() => Math.max(1, ...safeBins.value));

const bars = computed(() => {
  const describeShare = (count) => {
    const n = Number(props.sampleSize || 0);
    if (!Number.isFinite(n) || n <= 0) return null;
    const pct = Math.round((Number(count || 0) / n) * 100);
    return `${pct}%`;
  };

  return safeBins.value.map((count, idx) => {
    const start = idx * 5;
    const end = idx === 20 ? 100 : start + 4;
    const range = start === end ? `${start}%` : `${start}–${end}%`;
    const share = describeShare(count);
    const tooltip = share ? `${range}: ${share} of users` : `${range}`;
    return {
      count,
      tooltip,
      heightPct: Math.round((count / maxCount.value) * 100),
    };
  });
});

const clamp01 = (n) => Math.min(1, Math.max(0, Number(n) || 0));

const userX = computed(() => clamp01(props.userScore / 100) * 100);
const passMarkX = computed(() => clamp01(props.passMark / 100) * 100);
const userScoreRounded = computed(() =>
  props.userScore == null ? null : Math.round(props.userScore * 10) / 10,
);
</script>

<style scoped>
.normogram__chart {
  position: relative;
  width: 100%;
}

.normogram__bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 140px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 10px 4px 6px 4px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
}

.normogram__bar {
  flex: 1 1 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
}

.normogram__barFill {
  width: 100%;
  background: rgba(3, 4, 94, 0.55);
  border-radius: 3px 3px 0 0;
}

.normogram__marker {
  position: absolute;
  top: 0;
  bottom: 22px;
  width: 2px;
  transform: translateX(-1px);
  border-radius: 1px;
  opacity: 0.95;
}

.normogram__marker--user {
  background: rgba(33, 150, 243, 0.9);
}

.normogram__marker--pass {
  background: rgba(76, 175, 80, 0.9);
}

.normogram__axis {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  padding: 4px 2px 0 2px;
}

.normogram__meta {
  margin-top: 10px;
}

.normogram__stats {
  display: grid;
  gap: 6px;
}

.normogram__statRow {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.normogram__statLabel {
  color: rgba(0, 0, 0, 0.6);
}

.normogram__statValue {
  font-weight: 600;
}

.normogram__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  display: inline-block;
}

.normogram__dot--user {
  background: rgba(33, 150, 243, 0.9);
}

.normogram__dot--pass {
  background: rgba(76, 175, 80, 0.9);
}
</style>
